import React from 'react';
import { Target, Users, Scale, HandCoins, Handshake, Leaf, Radiation, Skull, Trophy, Shield } from 'lucide-react';

const GameEndingsSlide = ({ slide, slideKey, currentSlide, slidesLength }) => {
    const getDefeatIcon = (iconName) => {
        const icons = {
            target: <Target className="w-5 h-5 text-red-400" />,
            users: <Users className="w-5 h-5 text-red-400" />,
            scale: <Scale className="w-5 h-5 text-red-400" />,
            handcoins: <HandCoins className="w-5 h-5 text-red-400" />,
            handshake: <Handshake className="w-5 h-5 text-red-400" />,
            leaf: <Leaf className="w-5 h-5 text-red-400" />,
            radiation: <Radiation className="w-5 h-5 text-red-400" />,
            skull: <Skull className="w-5 h-5 text-red-400" />
        };
        return icons[iconName] || <Skull className="w-5 h-5 text-red-400" />;
    };

    return (
        <div key={slideKey} className="h-full p-8 flex flex-col relative overflow-hidden">
            {/* Background particles */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                {[...Array(15)].map((_, i) => (
                    <div
                        key={i}
                        className={`absolute w-1 h-1 rounded-full animate-ping ${i % 2 === 0 ? 'bg-red-400' : 'bg-blue-400'}`}
                        style={{
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                            animationDelay: `${Math.random() * 3}s`,
                            animationDuration: `${2 + Math.random() * 3}s`
                        }}
                    />
                ))}
            </div>

            {/* Header */}
            <div className="text-center mb-3 relative z-10">
                <h2 className="text-3xl font-bold mb-1 bg-gradient-to-r from-red-400 via-purple-400 to-blue-400 bg-clip-text text-transparent animate-fade-in-up">
                    {slide.title}
                </h2>
                <p className="text-lg text-blue-200/60 animate-fade-in-up stagger-1">
                    {slide.subtitle}
                </p>
            </div>

            {/* Split content */}
            <div className="flex-1 grid grid-cols-12 gap-4 relative z-10 min-h-0">
                {/* LEFT: Les 7 Défaites */}
                <div className="col-span-8 flex flex-col min-h-0">
                    <div className="flex items-center justify-center gap-2 mb-3">
                        <Skull className="w-5 h-5 text-red-500 animate-pulse" />
                        <span className="text-xl font-bold text-red-400">7 Chemins vers la défaite</span>
                    </div>

                    <div className="grid grid-cols-2 gap-2 flex-1 min-h-0">
                        {slide.defeats?.map((defeat, i) => (
                            <div
                                key={i}
                                className={`bg-gradient-to-br from-red-900/30 to-slate-900/50 border border-red-500/30 rounded-xl p-2 hover:border-red-400/50 hover:scale-[1.02] transition-all animate-fade-in-scale flex items-start gap-2`}
                                style={{ animationDelay: `${i * 0.1}s`, opacity: 0 }}
                            >
                                <div className="w-7 h-7 rounded-lg bg-red-500/20 flex items-center justify-center flex-shrink-0">
                                    {getDefeatIcon(defeat.icon)}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h4 className="text-sm font-bold text-red-300 mb-0.5">{defeat.title}</h4>
                                    <p className="text-xs text-slate-400 leading-relaxed">{defeat.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* RIGHT: La Victoire */}
                <div className="col-span-4 flex flex-col min-h-0">
                    <div className="flex items-center justify-center gap-2 mb-3">
                        <Trophy className="w-5 h-5 text-yellow-400 animate-bounce" />
                        <span className="text-xl font-bold text-blue-400">Un unique vers la Gloire</span>
                    </div>

                    <div className="flex-1 bg-gradient-to-br from-blue-900/40 to-slate-900/50 border-2 border-blue-500/40 rounded-2xl p-4 hover:border-blue-400/60 transition-all animate-fade-in-scale relative overflow-hidden"
                        style={{ animationDelay: '0.7s', opacity: 0 }}>
                        {/* Glow effect */}
                        <div className="absolute inset-0 bg-gradient-to-t from-blue-500/10 to-transparent" />

                        {/* Military parade animation */}
                        <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-blue-900 via-blue-500 to-blue-900 animate-pulse" />

                        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center">
                            <div className="mb-3">
                                <Shield className="w-10 h-10 text-blue-400 mx-auto mb-1" style={{ filter: 'drop-shadow(0 0 15px rgba(59, 130, 246, 0.5))' }} />
                                <Trophy className="w-8 h-8 text-yellow-400 mx-auto animate-pulse" style={{ filter: 'drop-shadow(0 0 15px rgba(250, 204, 21, 0.5))' }} />
                            </div>

                            <h3 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300 mb-2">
                                {slide.victory?.title || "VICTOIRE MILITAIRE"}
                            </h3>

                            <p className="text-base text-blue-200/80 leading-relaxed">
                                {slide.victory?.desc || "Éliminez toutes les nations concurrentes pour régner en maître absolu."}
                            </p>

                            <div className="mt-3 px-3 py-1 bg-yellow-500/20 border border-yellow-500/40 rounded-full">
                                <span className="text-xs font-mono text-yellow-300">UNIQUE CONDITION DE VICTOIRE</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GameEndingsSlide;
